// ===== COMMON TYPES =====
interface Contact {
  name: string
  phone: string
}

interface Location {
  latitude: number
  longitude: number
}

// ===== PROVINSI/KOTA/KAB INSTITUTION =====
export interface TableDataItem {
  district: string
  poktan_count: number
  member_poktan_count: number
  gapoktan_count: number
  kep_count: number
}

export interface PenyuluhTableData {
  id: string
  name: string
  type_extension_worker: string
}

export interface PenyuluhApiResponse {
  id?: string
  code: string
  province?: string
  city?: string
  institutional_type: string
  name: string
  address: string
  phone: string
  email: string
  website: string
  head_department: string
  head_department_phone: string
  work_unit: string
  division_name: string
  head_division: string
  head_division_phone: string
  section_name: string
  head_section_name: string
  head_section_phone: string
  coordinator_name: string
  coordinator_phone: string
  latitude: string | number | null
  longitude: string | number | null
  table_data: PenyuluhTableData[]
  table_meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

export interface InstitutionApiResponse {
  id?: string
  code: string
  province?: string
  city?: string
  institutional_type: string
  name: string
  address: string
  phone: string
  email: string
  website: string
  head_department: string
  head_department_phone: string
  work_unit: string
  division_name: string
  head_division: string
  head_division_phone: string
  section_name: string
  head_section_name: string
  head_section_phone: string
  coordinator_name: string
  coordinator_phone: string
  latitude: number | null
  longitude: number | null
  table_data?: TableDataItem[]
}

export interface Institution {
  id: string
  code: string
  province: string
  city?: string
  type: string
  nomenklatur: string
  address: string
  phone: string
  contacts: string[] // email, website
  head_of_department: Contact
  work_unit: string
  division: string
  head_of_division: Contact
  section: string
  head_of_section: Contact
  coordinator: Contact
  location: Location | null
  table_data?: TableDataItem[]
}

export function mapToInstitution(data: InstitutionApiResponse): Institution {
  return {
    id: data.id ?? data.code,
    code: data.code,
    province: data.province ?? '',
    city: data.city,
    type: data.institutional_type,
    nomenklatur: data.name,
    address: data.address,
    phone: data.phone,
    contacts: [data.email, data.website].filter(Boolean),
    head_of_department: {
      name: data.head_department,
      phone: data.head_department_phone
    },
    work_unit: data.work_unit,
    division: data.division_name,
    head_of_division: {
      name: data.head_division,
      phone: data.head_division_phone
    },
    section: data.section_name,
    head_of_section: {
      name: data.head_section_name,
      phone: data.head_section_phone
    },
    coordinator: {
      name: data.coordinator_name,
      phone: data.coordinator_phone
    },
    location: toLocation(data.latitude, data.longitude),
    table_data: data.table_data
  }
}

function toLocation(
  latitude: string | number | null | undefined,
  longitude: string | number | null | undefined
): Location | null {
  if (latitude == null || longitude == null || latitude === '' || longitude === '') {
    return null
  }

  const lat = Number(latitude)
  const lng = Number(longitude)

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return null
  }

  return { latitude: lat, longitude: lng }
}

// ===== BPP (BALAI PENYULUHAN PERTANIAN) =====
export interface BppApiResponse {
  id: string
  name: string
  institutional_type: string
  work_area: Array<{
    code?: string
    name: string
  }>
  address: string
  phone: string
  telp?: string
  email?: string
  website?: string
  latitude: number
  longitude: number
  name_leader?: string
  phone_leader?: string
  name_coordinator?: string
  phone_coordinator?: string
  total_penyuluh?: number
  total_gapoktan?: number
  total_poktan?: number
  koordinator_penyuluh?: {
    jenis?: string | null
    nama?: string | null
    nip?: string | null
  } | null
  is_administrative?: boolean | null
}

export interface BppCoordinator {
  jenis: string
  nama: string
  nip: string
}

export interface Bpp {
  id: string
  code?: string
  name: string
  type: string
  work_regions: { code: string; name: string }[]
  address: string
  phone: string
  contacts: string[]
  location: Location
  head: Contact
  coordinator: Contact
  koordinator_penyuluh: BppCoordinator | null
  is_administrative: boolean | null
  total_penyuluh?: number
  total_gapoktan?: number
  total_poktan?: number
}

export function mapToBpp(data: BppApiResponse): Bpp {
  return {
    id: data.id,
    name: data.name,
    type: data.institutional_type,
    work_regions: data.work_area.map(region => ({
      code: region.code ?? '',
      name: region.name
    })),
    address: data.address,
    phone: data.phone,
    contacts: [data.email, data.website].filter(Boolean) as string[],
    location: {
      latitude: data.latitude,
      longitude: data.longitude
    },
    head: {
      name: data.name_leader ?? '',
      phone: data.phone_leader ?? ''
    },
    coordinator: {
      name: data.name_coordinator ?? '',
      phone: data.phone_coordinator ?? ''
    },
    koordinator_penyuluh: data.koordinator_penyuluh
      ? {
          jenis: data.koordinator_penyuluh.jenis ?? '',
          nama: data.koordinator_penyuluh.nama ?? '',
          nip: data.koordinator_penyuluh.nip ?? ''
        }
      : null,
    is_administrative:
      typeof data.is_administrative === 'boolean'
        ? data.is_administrative
        : null,
    total_penyuluh: data.total_penyuluh,
    total_gapoktan: data.total_gapoktan,
    total_poktan: data.total_poktan
  }
}

// ===== POSLUHDES =====
export interface PosluhdesApiResponse {
  id: string
  province?: string | null
  city?: string | null
  district?: string | null
  village: string
  name_posluhdes: string
  address: string
  established_year: number
  chairman: string
  secretary: string
  treasurer: string
  member_count: number
  name_extension_worker: string
}

export interface Posluhdes {
  id: string
  name: string
  province: string
  city: string
  district: string
  village: string
  address: string
  year_established: number
  chairman: string
  secretary: string
  treasurer: string
  extension_worker: string
  total_members: number
}

export function mapToPosluhdes(data: PosluhdesApiResponse): Posluhdes {
  return {
    id: data.id,
    name: data.name_posluhdes,
    province: data.province ?? '',
    city: data.city ?? '',
    district: data.district ?? '',
    village: data.village,
    address: data.address,
    year_established: data.established_year,
    chairman: data.chairman ?? '',
    secretary: data.secretary ?? '',
    treasurer: data.treasurer ?? '',
    extension_worker: data.name_extension_worker ?? '',
    total_members: data.member_count
  }
}

export interface DetailBpp {
  id: string
  name: string
  address: string
  province_code: string
  province: string
  city_code: string
  city: string
  district_code: string
  district: string
  bpp_classification: string
  building_status: string
  building_condition: string
  latitude: string
  longitude: string
  name_coordinator?: string
  status_coordinator?: string
  phone_coordinator?: string
  work_area: {
    name: string
  }[]
}

export interface DetailPosluhdes {
  id: string
  name: string
  province_code: string
  province: string
  city_code: string
  city: string
  district_code: string
  district: string
  village_code: string
  village: string
  name_posluhdes: string
  address: string
  established_year: number
  chairman: string
  chairman_phone: string
  secretary: string
  secretary_phone: string
  treasurer: string
  treasurer_phone: string
  member_count: number
  name_extension_worker: string
}

// ===== DETAIL INSTITUTION (PROVINCE/CITY) =====
// Using same type as Institution since the API returns the same structure
export type DetailInstitution = Institution
